import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Button,
    IconButton,
    Box,
    Typography,
    TextField,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';
import EnumsService from '../../../../Services/enums.service';
import { toast } from 'react-toastify';

const VerifoneImportModal = ({
    open,
    onClose,
    caseId,
    accountantId,
    token,
    onSuccess,
    previewVerifone,
    importVerifone,
    sortCodeIdOptions = [],
    accountCardOptions: accountCardOptionsProp = [],
    client,
    setAccountCardOptions,
    t,
    direction = 'rtl',
}) => {
    const [file, setFile] = useState(null);
    const [defaultSortCodeId, setDefaultSortCodeId] = useState('3');
    const [creditAccountId, setCreditAccountId] = useState('');
    const [debitAccountId, setDebitAccountId] = useState('');
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [localAccountCardOptions, setLocalAccountCardOptions] = useState([]);

    const effectiveCaseId = client?.caseId ?? caseId;
    const accountCardOptions = localAccountCardOptions.length > 0 ? localAccountCardOptions : accountCardOptionsProp;

    useEffect(() => {
        if (!open || !effectiveCaseId || !token) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await EnumsService.GetAccountCardsByCaseId(effectiveCaseId, token);
                if (cancelled || !res?.ok) return;
                const data = await res.json();
                const next = [{ name: t('AddAccountCard'), value1: 'AddItem' }, ...(data || [])];
                setLocalAccountCardOptions(next);
                if (setAccountCardOptions) setAccountCardOptions(next);
            } catch (e) {
                if (!cancelled) console.error('Fetch account cards failed:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [open, effectiveCaseId, token, t, setAccountCardOptions]);

    const cardsWithoutAddItem = useMemo(
        () => (accountCardOptions || []).filter((c) => c.value1 !== 'AddItem'),
        [accountCardOptions]
    );

    const accountOptions = useMemo(
        () =>
            cardsWithoutAddItem.map((c) => ({
                ...c,
                cardId: c.cardId ?? c.value1,
                label: `${c.name ?? ''} - ${c.cardId ?? c.value1 ?? ''}`.trim(),
            })),
        [cardsWithoutAddItem]
    );

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData(null);
        setPage(0);
        setRowsPerPage(10);
        onClose();
    };

    const runPreview = useCallback(async () => {
        if (!file || !effectiveCaseId || !accountantId || !token || !previewVerifone) return;
        try {
            setProcessing(true);
            const res = await previewVerifone(effectiveCaseId, accountantId, file, token);
            setPreviewData({
                entries: res.entries ?? [],
                totalLines: res.totalLines ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
                errorMessages: res.errorMessages ?? [],
            });
            setPage(0);
        } catch (err) {
            console.error('Verifone preview failed:', err);
            setPreviewData({
                entries: [],
                totalLines: 0,
                validCount: 0,
                errorCount: 1,
                errorMessages: [err?.message || 'Preview failed'],
            });
        } finally {
            setProcessing(false);
        }
    }, [file, effectiveCaseId, accountantId, token, previewVerifone]);

    const handleCommit = async () => {
        if (!file || !effectiveCaseId || !token || !importVerifone) return;
        if (!creditAccountId || !debitAccountId) {
            toast.warning(t('VerifoneSelectAccounts') || 'Please select קופות (credit) and credit card (debit) accounts.');
            return;
        }
        try {
            setProcessing(true);
            const res = await importVerifone(
                effectiveCaseId,
                file,
                defaultSortCodeId || '3',
                creditAccountId,
                debitAccountId,
                token
            );
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const messages = res?.errorMessages ?? [];
            const duplicateCount = res?.duplicateCount ?? 0;
            const duplicateMessages = res?.duplicateMessages ?? [];
            if (errors > 0 && messages.length > 0) {
                toast.error(`${t('ImportFailed')} – ${t('processed')}: ${processed}, ${t('Errors')}: ${errors}`);
            } else if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}`);
            } else {
                let msg = processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful');
                if (duplicateCount > 0) {
                    msg += `. ${duplicateCount} ${t('DuplicatesSkipped') || 'duplicates skipped'}`;
                    duplicateMessages.slice(0, 3).forEach((m) => toast.info(m));
                }
                toast.success(msg);
            }
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('Verifone import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setProcessing(false);
        }
    };

    if (!open) return null;

    const entries = previewData?.entries ?? [];
    const validEntries = entries.filter((e) => e.isValid);
    const sumAmount = validEntries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    const canImport = !!previewData && !processing && (previewData.validCount ?? 0) > 0 && !!creditAccountId && !!debitAccountId;
    const paginatedEntries = entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={(e) => e.stopPropagation()}>
                <div
                    className={`download-modal-content ${direction}`}
                    style={{ position: 'relative', maxWidth: previewData ? '1100px' : '560px' }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <h4 className="report-title">
                            {t('VerifoneImport') || 'Verifone Credit Terminal'} – {t('Preview')} & {t('Import')}
                        </h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box display="flex" flexDirection="column" gap={2}>
                        <div>
                            <h5>.xlsx</h5>
                            <DropzoneArea
                                key={file ? file.name : 'verifone-empty'}
                                onDrop={(files) => {
                                    if (files?.length) setFile(files[0]);
                                    setPreviewData(null);
                                }}
                                accept=".xlsx,.xls"
                            />
                            {file && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    {t('FileSelected')}: {file.name}
                                </Typography>
                            )}
                        </div>

                        <Box display="flex" flexWrap="wrap" gap={2}>
                            {sortCodeIdOptions?.length > 0 ? (
                                <Autocomplete
                                    options={sortCodeIdOptions}
                                    getOptionLabel={(o) => `${o.sortCodeId} - ${o.sortCodeName ?? ''}`}
                                    value={sortCodeIdOptions.find((o) => String(o.sortCodeId) === String(defaultSortCodeId)) || null}
                                    onChange={(_, v) => setDefaultSortCodeId(v ? String(v.sortCodeId) : '3')}
                                    size="small"
                                    sx={{ minWidth: 200 }}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('DefaultSortCode') || 'Sort code'} variant="standard" />
                                    )}
                                />
                            ) : (
                                <TextField
                                    label={t('DefaultSortCode') || 'Sort code'}
                                    value={defaultSortCodeId}
                                    onChange={(e) => setDefaultSortCodeId(e.target.value)}
                                    size="small"
                                    sx={{ minWidth: 120 }}
                                    variant="standard"
                                />
                            )}
                            <Autocomplete
                                options={accountOptions}
                                getOptionLabel={(o) => o.label || o.cardId || ''}
                                value={accountOptions.find((o) => String(o.cardId) === String(creditAccountId)) || null}
                                onChange={(_, v) => setCreditAccountId(v ? String(v.cardId) : '')}
                                size="small"
                                sx={{ minWidth: 240 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('VerifoneCreditAccount') || 'קופות (Credit)'}
                                        variant="standard"
                                    />
                                )}
                            />
                            <Autocomplete
                                options={accountOptions}
                                getOptionLabel={(o) => o.label || o.cardId || ''}
                                value={accountOptions.find((o) => String(o.cardId) === String(debitAccountId)) || null}
                                onChange={(_, v) => setDebitAccountId(v ? String(v.cardId) : '')}
                                size="small"
                                sx={{ minWidth: 240 }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('VerifoneDebitAccount') || 'Credit card (Debit)'}
                                        variant="standard"
                                    />
                                )}
                            />
                        </Box>

                        <Typography variant="caption" color="text.secondary">
                            {t('VerifoneImportHint') ||
                                'Sort code 3, VAT 0. Credit = קופות, Debit = credit card clearing account.'}
                        </Typography>

                        <Box display="flex" gap={1.5}>
                            <Button variant="contained" onClick={runPreview} disabled={!file || processing}>
                                {processing && !previewData ? t('Processing') : t('Preview')}
                            </Button>
                            <Button variant="contained" color="success" onClick={handleCommit} disabled={!canImport}>
                                {processing && previewData ? t('Processing') : t('Import')}
                            </Button>
                        </Box>
                    </Box>

                    {previewData && (
                        <Box mt={3} sx={{ maxHeight: 600, overflow: 'auto' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('TotalLines') || 'Total lines'}: <strong>{previewData.totalLines}</strong>
                                {' | '}
                                {t('ValidCount') || 'Valid'}: <strong>{previewData.validCount}</strong>
                                {' | '}
                                {t('ErrorCount') || 'Errors'}: <strong>{previewData.errorCount}</strong>
                                {' | '}
                                {t('SumAmount') || 'Sum'}: <strong>{formatAmount(sumAmount)}</strong>
                            </Typography>

                            {previewData.errorMessages?.length > 0 && (
                                <Box mb={2} p={1.5} bgcolor="error.light" borderRadius={1} sx={{ maxHeight: 180, overflow: 'auto' }}>
                                    <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                        {t('Errors') || 'Errors'} ({previewData.errorMessages.length})
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {previewData.errorMessages.slice(0, 20).map((msg, i) => (
                                            <li key={i}>
                                                <Typography variant="body2" component="span">{msg}</Typography>
                                            </li>
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {entries.length > 0 && (
                                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DocumentDate')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Reference')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Description')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('VerifoneAcquirer') || 'Acquirer'}</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{t('Amount')}</TableCell>
                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginatedEntries.map((row, i) => (
                                                <TableRow
                                                    key={row.lineNumber ?? i}
                                                    sx={{ bgcolor: row.isValid ? 'transparent' : 'action.hover' }}
                                                >
                                                    <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.documentDate ?? '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.reference || row.transactionId || '—'}</TableCell>
                                                    <TableCell sx={{ fontSize: 12, maxWidth: 240, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {row.description ?? '—'}
                                                    </TableCell>
                                                    <TableCell sx={{ fontSize: 12 }}>{row.acquirer ?? '—'}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(row.amount)}</TableCell>
                                                    <TableCell sx={{ fontSize: 12, maxWidth: 280, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        {row.isValid ? (
                                                            <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>
                                                        ) : (
                                                            <Typography variant="body2" color="error.main">{row.errorMessage ?? ''}</Typography>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        component="div"
                                        count={entries.length}
                                        page={page}
                                        onPageChange={(_, p) => setPage(p)}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={(e) => {
                                            setRowsPerPage(parseInt(e.target.value, 10));
                                            setPage(0);
                                        }}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                    />
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerifoneImportModal;
